/**
 * The backend writes the placeholder "(running)" as the summary of a timer
 * segment that was closed without a summary (`timer.service.ts`, never
 * changed from the UI). It is not user text, so the UI shows a localized,
 * quiet hint instead (owner-approved, M5).
 */
export const RUNNING_SUMMARY_PLACEHOLDER = '(running)';

/** True for the backend placeholder (whitespace and case tolerant). */
export function isRunningPlaceholder(summary: string | null | undefined): boolean {
  return typeof summary === 'string' && summary.trim().toLowerCase() === RUNNING_SUMMARY_PLACEHOLDER;
}

/**
 * What to render for a work-log summary:
 * - `text`: the user's own summary;
 * - `running`: the timer segment is still open (no end time) → "טיימר פעיל";
 * - `timerNoSummary`: a closed timer segment that never got a summary;
 * - `empty`: no summary at all.
 */
export type SummaryDisplay =
  | { kind: 'text'; text: string }
  | { kind: 'running' }
  | { kind: 'timerNoSummary' }
  | { kind: 'empty' };

export function summaryDisplay(summary: string | null | undefined, opts: { ended?: boolean } = {}): SummaryDisplay {
  if (isRunningPlaceholder(summary)) return opts.ended === false ? { kind: 'running' } : { kind: 'timerNoSummary' };
  const text = (summary ?? '').trim();
  return text ? { kind: 'text', text } : { kind: 'empty' };
}

/** The value an edit form should start from: the placeholder is not a summary to edit. */
export function editableSummary(summary: string | null | undefined): string {
  return isRunningPlaceholder(summary) ? '' : summary ?? '';
}

/**
 * HN-2: a timer segment closed by "עצירה" / a break has no summary of its own (the "(running)"
 * placeholder). Editing only its times must not demand a summary - the form asks for one (5+ chars)
 * only when the entry already has one, or when the person starts writing one.
 */
export function summaryRequiredOnEdit(loadedSummary: string | null | undefined): boolean {
  return editableSummary(loadedSummary).trim() !== '';
}
